// The outbound-email attachment budget, and the pure helpers that go
// with it.
//
// Deliberately a leaf module: no imports at all. The composer (web) and
// the send/draft routes (Workers) both need these numbers, and they
// have to agree — a UI that guesses a different limit than the server
// enforces is a UI that surprises someone at Send. Keeping it here
// rather than in outbound-attachments.ts means the browser can import
// it without dragging the Graph clients (and their Worker-only fetch
// typings) into the web tsconfig program.
//
// ─── Two numbers, and why ─────────────────────────────────────────
//
// EMAIL_ATTACHMENT_TOTAL_BYTES (20 MB) is the real limit: the most
// file an advisor may put on one email. Every piece of copy the team
// reads quotes this one.
//
// EMAIL_ATTACHMENT_SINGLE_REQUEST_BYTES (2.75 MB) is an internal fork
// in the road, not a limit anyone is told about. It answers one
// question — "does this fit, base64-encoded, inside one Graph
// request?" — which the send path asks twice:
//
//   * Of the WHOLE MESSAGE, to pick a send flow. Under it: POST
//     /me/sendMail, the entire message as ONE request, which Graph
//     caps at 4 MB. Base64 inflates bytes by a third, so 2.75 MB of
//     file encodes to ~3.67 MB and leaves ~330 KB for the subject,
//     body, signature, and JSON envelope. One round trip; the fast
//     path, and the common one. Over it: create the message as a
//     draft, push each file up separately, then send the draft. More
//     round trips, no 4 MB ceiling. This is what Outlook on the web
//     does, which is why a file the OS refused uploaded there without
//     complaint.
//   * Of EACH FILE inside that draft flow, to pick how it goes on.
//     Under it: POST /messages/{id}/attachments, one request. Over it:
//     an upload session, chunked. Microsoft's guidance says to switch
//     at 3 MB, but 3 MB base64-encodes to 4,194,304 bytes — Graph's
//     request ceiling exactly, before the JSON envelope. Following
//     that guidance literally would send a request Graph refuses, so
//     the fork sits where the arithmetic actually clears.
//
// [History: until 2026-08-25 only the first path existed, so 2.75 MB
// WAS the limit, and the composer told advisors it was "Outlook's".
// Nate Swartz hit it with a 9.2 MB inspection report — a file Outlook
// web took without blinking — and said what the code comment here had
// been waiting to hear: _"we attach large files all the time."_ The
// number was ours, not Microsoft's, and the copy naming Outlook for it
// was the part that wasted his afternoon.]
//
// ─── Why 20 MB and not more ───────────────────────────────────────
//
// Microsoft caps the whole MESSAGE per tenant — 35 MB on a default
// Microsoft 365 tenant, configurable up to 150 MB. Attachments are
// base64-encoded on the wire, so 20 MB of files is ~27 MB of message:
// comfortably under a 35 MB ceiling with room for the body. Raising
// this is a config question (check the tenant's actual send limit),
// not a rebuild — the flow above has no 20 MB in it anywhere.
//
// If Exchange rejects a send anyway (a tenant capped lower than we
// assume), the advisor gets Microsoft's own refusal translated into
// plain English rather than a generic failure — see
// outbound-large-send.ts.
export const EMAIL_ATTACHMENT_TOTAL_BYTES = 20 * 1024 * 1024;

// The fork described above. Not a limit — a routing decision, and one
// number rather than two because both uses are the same physical
// constraint asked at different scopes.
// tests/email/outbound-attachments.test.ts asserts the RELATIONSHIP to
// Graph's 4 MB request cap rather than the literal, so the arithmetic
// can't quietly drift back to the ceiling.
//   https://learn.microsoft.com/en-us/graph/api/message-post-attachments
//   https://learn.microsoft.com/en-us/graph/outlook-large-attachments
export const EMAIL_ATTACHMENT_SINGLE_REQUEST_BYTES = 2.75 * 1024 * 1024;

/** Human-readable size, for copy the advisor reads. */
export function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Exact decoded byte length of a base64 string WITHOUT allocating the
 * bytes — so a file that arrived inline can be measured against the
 * same budget as one the server fetched. Returns 0 for malformed input.
 */
export function base64ByteLength(b64: string): number {
  const clean = b64.replace(/\s/g, "");
  if (clean.length === 0 || clean.length % 4 !== 0) return 0;
  const padding = clean.endsWith("==") ? 2 : clean.endsWith("=") ? 1 : 0;
  return (clean.length * 3) / 4 - padding;
}
